// Module-level auth store for the portal's multi-user session login.
//
// A subscribable singleton keeps `useAuth()` usable anywhere (sidebar, route
// guard, login page, change-password page) with no context/provider wrapper.
// Access control lives in the backend (AUTH_MODE=session); this mirrors the
// logged-in user + "must change password" state into React and drives redirects.
// The browser holds the session as an HttpOnly cookie — no token is stored here.

type Status = 'loading' | 'authenticated' | 'unauthenticated';

export interface AuthUser {
  id?: string;
  email: string;
  name: string;
  role?: string;
  must_change_password?: boolean;
}

interface AuthState {
  status: Status;
  user: AuthUser | null;
}

let state: AuthState = { status: 'loading', user: null };
const listeners = new Set<() => void>();

function setState(next: AuthState): void {
  state = next;
  listeners.forEach((l) => l());
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getState(): AuthState {
  return state;
}

interface RawUser {
  id?: string;
  email?: string;
  name?: string;
  role?: string;
  must_change_password?: boolean;
}

function toUser(raw: RawUser | null | undefined): AuthUser {
  return {
    id: raw?.id,
    email: raw?.email || '',
    name: raw?.name || '',
    role: raw?.role,
    must_change_password: raw?.must_change_password ?? false,
  };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => ({}));
  return (data && (data.error as string)) || fallback;
}

let checkStarted = false;

// Runs once (guarded) on first mount: probes /api/me to learn whether the
// browser already holds a valid session cookie, and who the user is.
export async function ensureSessionChecked(): Promise<void> {
  if (checkStarted) return;
  checkStarted = true;
  try {
    const res = await fetch('/api/me', { credentials: 'include' });
    if (res.ok) {
      const data = await res.json().catch(() => ({}));
      setState({ status: 'authenticated', user: toUser(data.user) });
    } else {
      setState({ status: 'unauthenticated', user: null });
    }
  } catch {
    setState({ status: 'unauthenticated', user: null });
  }
}

export async function login(email: string, password: string): Promise<void> {
  const res = await fetch('/api/login', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Invalid email or password'));
  }
  const data = await res.json().catch(() => ({}));
  setState({ status: 'authenticated', user: toUser(data.user) });
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const res = await fetch('/api/change-password', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Could not change password'));
  }
  const data = await res.json().catch(() => ({}));
  setState({ status: 'authenticated', user: toUser(data.user) });
}

export async function logout(): Promise<void> {
  try {
    await fetch('/api/logout', { method: 'POST', credentials: 'include' });
  } finally {
    setState({ status: 'unauthenticated', user: null });
    if (typeof window !== 'undefined') window.location.assign('/login');
  }
}

// Called by Apollo's error link when a GraphQL request 401s (session expired
// mid-use): flip state so guards redirect on the next render.
export function markUnauthenticated(): void {
  setState({ status: 'unauthenticated', user: null });
}

// Test helper — reset the module singleton between cases.
export function __resetAuthStoreForTests(): void {
  state = { status: 'loading', user: null };
  checkStarted = false;
  listeners.clear();
}
